/**
 * Единая точка входа для отправки «директивы создателя» через Падлу.
 *
 * Эта утилита используется ВЕЗДЕ в panel где admin может попросить бота
 * что-то сообщить или сделать в чате юзера:
 *   • NerveCenter user actionModal
 *   • NerveCenter UserCard (галактика — карточка-окошко справа)
 *   • NerveCenter UserProfile (Manage tab)
 *   • Viewer chat workspace header (вкладка чата)
 *   • любые будущие точки
 *
 * НЕ используем raw fetch в callsites — иначе с_with_tools выкатится
 * только в одном месте, а в других забудут. Один helper, один payload,
 * один источник истины.
 *
 * Единая машина (2 сен 2026): любая директива — ход общего конвейера Падлы
 * как заметка режиссёра. `with_tools` лишь разрешает инструменты (нарисуй /
 * агент / поиск), `output_mode` выбирает канал — все сочетания законны и
 * идут одним путём; ответ сервера — честный исход по факту хода: отправлено
 * (номер, текст, канал), показана карточка (агент ждёт человека), отложено
 * (человек недостижим), в очереди (ход за живым разговором), выполнено без
 * сообщения.
 */
export interface SendCreatorDirectiveArgs {
  /** Например, '/api/v1' (panel-backend prefix). */
  apiBase: string;
  /** Auth headers (`Authorization: Bearer ...`). */
  headers: Record<string, string>;
  /** Telegram chat_id куда отправить. Для приваток = user_id. */
  chatId: number;
  /** Telegram user_id юзера-получателя. */
  userId: number;
  /** Текст директивы — Падла перефразирует в своём стиле. */
  directive: string;
  /** true — Падле разрешены инструменты (нарисуй, отредактируй, запусти агента, поищи). */
  withTools: boolean;
  /** `voice` — Падла скажет это голосовым сообщением (совместимо с withTools). */
  outputMode?: 'text' | 'voice';
}

export interface CreatorDirectiveResult {
  ok: boolean;
  /** Текст, который Падла реально отправил (или отложил). */
  text?: string;
  /** Модель хода. */
  model?: string;
  /** Длительность хода. */
  totalMs?: number;
  /** Ход закончился карточкой (запуск агента): Падла ждёт ответа человека. */
  card?: boolean;
  cardPrompt?: string;
  /** Ход выполнен, прямого сообщения человеку не было (модуль/тихая ветка). */
  handled?: boolean;
  /** 202: ход стоит за живым разговором дольше бюджета ожидания — выполнится сам. */
  queued?: boolean;
  /** Trace id для дальнейшей диагностики. */
  traceId?: string;
  /** HTTP статус. */
  status: number;
  /** Текст ошибки от gateway (если ok=false). */
  error?: string;
  /**
   * 202: текст готов, но человек недостижим (заблокировал бота / не открыл диалог) —
   * директива ЖДЁТ в очереди и уйдёт сама при возвращении (до 7 дней).
   */
  deferred?: boolean;
  deferredReason?: 'blocked' | 'no_chat';
  /** Каким каналом РЕАЛЬНО ушло (гейтвей отвечает по факту: голос мог откатиться в текст). */
  channel?: 'text' | 'voice';
  /** 422: голос отвергнут приватностью человека, плашка-объяснение ему доставлена. */
  voiceForbidden?: boolean;
}

interface ServerResponse {
  ok?: boolean;
  message_id?: number;
  text?: string;
  model?: string;
  total_ms?: number;
  trace_id?: string;
  error?: string;
  deferred?: boolean;
  queued?: boolean;
  card?: boolean;
  prompt_text?: string;
  handled?: boolean;
  reason?: string;
  channel?: string;
  voice_forbidden?: boolean;
}

export async function sendCreatorDirective(
  args: SendCreatorDirectiveArgs
): Promise<CreatorDirectiveResult> {
  const { apiBase, headers, chatId, userId, directive, withTools, outputMode } = args;
  const trimmed = (directive ?? '').trim();
  if (!trimmed) {
    return { ok: false, status: 0, error: 'Пустая директива' };
  }
  let res: Response;
  try {
    res = await fetch(`${apiBase}/v1/admin/creator-message`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chat_id: chatId,
        user_id: userId,
        directive: trimmed,
        with_tools: withTools,
        output_mode: outputMode === 'voice' ? 'voice' : 'text',
      }),
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { ok: false, status: 0, error: msg };
  }

  let data: ServerResponse | null = null;
  try {
    data = (await res.json()) as ServerResponse;
  } catch {
    // gateway мог вернуть пустое тело — оставим data=null.
  }

  if (!res.ok) {
    return {
      ok: false,
      status: res.status,
      error: data?.error ?? `HTTP ${res.status}`,
      ...(res.status === 422 && data?.voice_forbidden ? { voiceForbidden: true } : {}),
    };
  }

  return {
    ok: true,
    status: res.status,
    text: data?.text,
    model: data?.model,
    totalMs: data?.total_ms,
    traceId: data?.trace_id,
    ...(data?.channel === 'voice' || data?.channel === 'text' ? { channel: data.channel } : {}),
    ...(data?.card ? { card: true, cardPrompt: data.prompt_text } : {}),
    ...(data?.handled ? { handled: true } : {}),
    ...(res.status === 202 && data?.queued ? { queued: true } : {}),
    ...(res.status === 202 && data?.deferred
      ? {
          deferred: true,
          deferredReason: data.reason === 'no_chat' ? ('no_chat' as const) : ('blocked' as const),
        }
      : {}),
  };
}

/** Удобный шорткат — одна строка success-toast'а. */
export function describeCreatorDirectiveResult(r: CreatorDirectiveResult): string {
  if (!r.ok) return `Ошибка: ${r.error ?? r.status}`;
  if (r.deferred) return describeDeferred(r);
  if (r.queued) return describeQueued();
  if (r.card) return describeCard(r);
  if (r.handled) return 'Падла выполнил поручение без отдельного сообщения';
  const model = r.model ?? 'LLM';
  const ms = r.totalMs != null ? `${r.totalMs}ms` : '?';
  return r.channel === 'voice'
    ? `Падла отправил голосом (${model}, ${ms})`
    : `Падла отправил (${model}, ${ms})`;
}

/** Ход стоит за живым разговором — правда о судьбе, а не «отправлено». */
export function describeQueued(): string {
  return 'Падла сейчас занят разговором с этим человеком — директива стоит следом и уйдёт сама. Смотри ленту чата.';
}

/** Ход закончился карточкой: Падла ждёт решения человека. */
export function describeCard(r: CreatorDirectiveResult): string {
  const p = (r.cardPrompt ?? '').trim();
  return p
    ? `Падла показал карточку и ждёт ответа: «${p.slice(0, 120)}»`
    : 'Падла показал карточку и ждёт ответа человека';
}

/** Одна строка про отложенную директиву — правда о судьбе, а не «отправлено». */
export function describeDeferred(r: CreatorDirectiveResult): string {
  return r.deferredReason === 'no_chat'
    ? 'Человек ещё не открыл бота — директива ждёт и уйдёт сама, как только он напишет (до 7 дней)'
    : 'Заблокировал бота — директива ждёт и уйдёт сама при разблокировке (до 7 дней)';
}
